import { LucideIcon, Eye, Edit, Shield, Lock, Users, Key, FileText, Database } from 'lucide-react';

export type PolicyCategory = 'access' | 'ownership' | 'data-protection' | 'role-based';

export interface PolicyTemplate {
  id: string;
  name: string;
  description: string;
  category: PolicyCategory;
  icon: LucideIcon;
  cedarPolicy: string;
  // Placeholders for customization
  placeholders?: {
    key: string;
    label: string;
    defaultValue: string;
    options?: string[];
  }[];
  tags: string[];
}

export interface ParsedPolicyState {
  effect: 'permit' | 'forbid';
  principal: string;
  action: string;
  resource: string;
  conditions: { field: string; op: string; value: string }[];
}

// Parse Cedar policy text into UI state for PolicyBuilder
export function parseCedarToState(cedarText: string): Partial<ParsedPolicyState> {
  const state: Partial<ParsedPolicyState> = {
    conditions: []
  };

  // Parse effect
  if (cedarText.startsWith('permit')) {
    state.effect = 'permit';
  } else if (cedarText.startsWith('forbid')) {
    state.effect = 'forbid';
  }

  // Parse action
  const actionMatch = cedarText.match(/action\s*==\s*Action::"(\w+)"/);
  if (actionMatch) {
    state.action = actionMatch[1];
  } else {
    state.action = 'any';
  }

  // Parse resource
  const resourceMatch = cedarText.match(/resource\s+is\s+([\w:]+)/);
  if (resourceMatch) {
    state.resource = resourceMatch[1];
  } else {
    state.resource = 'any';
  }

  // Parse role from context.userRoles.contains
  const roleMatch = cedarText.match(/context\.userRoles\.contains\("(\w+)"\)/);
  if (roleMatch) {
    state.principal = roleMatch[1];
  } else {
    state.principal = 'any';
  }

  // Parse additional conditions from when clause
  const whenMatch = cedarText.match(/when\s*\{([^}]+)\}/s);
  if (whenMatch) {
    const whenClause = whenMatch[1];
    // Extract conditions (simple pattern matching)
    const conditionPatterns = whenClause.split('&&').map(c => c.trim()).filter(c => c);

    conditionPatterns.forEach(cond => {
      // Skip role conditions already handled
      if (cond.includes('userRoles.contains')) return;

      // Match patterns like "resource.ownerId == context.userId"
      const condMatch = cond.match(/(\S+)\s*(==|!=|<|>|<=|>=|contains|in)\s*(\S+)/);
      if (condMatch) {
        state.conditions!.push({
          field: condMatch[1],
          op: condMatch[2],
          value: condMatch[3].replace(/"/g, '')
        });
      }
    });
  }

  return state;
}

export const policyTemplates: PolicyTemplate[] = [
  // ACCESS TEMPLATES
  {
    id: 'read-only-viewer',
    name: 'Read-only Viewer',
    description: 'Allow users with viewer role to read all resources but not modify them.',
    category: 'access',
    icon: Eye,
    cedarPolicy: `permit (
  principal,
  action == Action::"read",
  resource
)
when {
  context.userRoles.contains("viewer")
};`,
    placeholders: [
      {
        key: 'role',
        label: 'Role',
        defaultValue: 'viewer',
        options: ['viewer', 'member', 'guest']
      }
    ],
    tags: ['read', 'viewer', 'basic']
  },
  {
    id: 'full-access-admin',
    name: 'Full Admin Access',
    description: 'Grant administrators complete access to all resources and actions.',
    category: 'role-based',
    icon: Shield,
    cedarPolicy: `permit (
  principal,
  action,
  resource
)
when {
  context.userRoles.contains("admin")
};`,
    tags: ['admin', 'full-access', 'superuser']
  },
  {
    id: 'manager-crud',
    name: 'Manager CRUD Access',
    description: 'Allow managers to create, read, update, and delete resources.',
    category: 'role-based',
    icon: Users,
    cedarPolicy: `permit (
  principal,
  action,
  resource
)
when {
  context.userRoles.contains("manager")
};`,
    placeholders: [
      {
        key: 'resource',
        label: 'Resource Type',
        defaultValue: 'any',
        options: ['Matricks::Workflow', 'Matricks::Contact', 'Matricks::Provider', 'any']
      }
    ],
    tags: ['manager', 'crud', 'management']
  },
  {
    id: 'developer-execute',
    name: 'Developer Execute Access',
    description: 'Allow developers to execute workflows and agent pipelines.',
    category: 'role-based',
    icon: Key,
    cedarPolicy: `permit (
  principal,
  action == Action::"execute",
  resource
)
when {
  context.userRoles.contains("developer")
};`,
    tags: ['developer', 'execute', 'workflows']
  },

  // OWNERSHIP TEMPLATES
  {
    id: 'owner-only-edit',
    name: 'Owner-only Edit',
    description: 'Allow users to edit only resources they own. Prevents modification of others\' content.',
    category: 'ownership',
    icon: Edit,
    cedarPolicy: `permit (
  principal,
  action == Action::"write",
  resource
)
when {
  resource.ownerId == context.userId
};`,
    tags: ['owner', 'edit', 'self-only']
  },
  {
    id: 'owner-only-delete',
    name: 'Owner-only Delete',
    description: 'Allow users to delete only their own resources.',
    category: 'ownership',
    icon: Edit,
    cedarPolicy: `permit (
  principal,
  action == Action::"delete",
  resource
)
when {
  resource.ownerId == context.userId
};`,
    tags: ['owner', 'delete', 'self-only']
  },
  {
    id: 'owner-full-control',
    name: 'Owner Full Control',
    description: 'Grant owners complete control over their own resources.',
    category: 'ownership',
    icon: Key,
    cedarPolicy: `permit (
  principal,
  action,
  resource
)
when {
  resource.ownerId == context.userId
};`,
    tags: ['owner', 'full-control', 'self-only']
  },

  // DATA PROTECTION TEMPLATES
  {
    id: 'sensitive-field-deny',
    name: 'Sensitive Data Protection',
    description: 'Deny access to sensitive fields like SSN, salary, or personal identifiers.',
    category: 'data-protection',
    icon: Lock,
    cedarPolicy: `forbid (
  principal,
  action == Action::"read",
  resource is Matricks::Field
)
when {
  resource.fieldName == "ssn" ||
  resource.fieldName == "salary" ||
  resource.fieldName == "password"
};`,
    placeholders: [
      {
        key: 'fields',
        label: 'Sensitive Fields',
        defaultValue: 'ssn, salary, password'
      }
    ],
    tags: ['sensitive', 'pii', 'field-level', 'deny']
  },
  {
    id: 'admin-sensitive-access',
    name: 'Admin Sensitive Access',
    description: 'Allow only admins to access sensitive fields.',
    category: 'data-protection',
    icon: Shield,
    cedarPolicy: `permit (
  principal,
  action == Action::"read",
  resource is Matricks::Field
)
when {
  context.userRoles.contains("admin") &&
  resource.isSensitive == true
};`,
    tags: ['admin', 'sensitive', 'field-level']
  },
  {
    id: 'org-data-isolation',
    name: 'Organization Data Isolation',
    description: 'Restrict access to resources within the user\'s organization only.',
    category: 'data-protection',
    icon: Database,
    cedarPolicy: `permit (
  principal,
  action == Action::"read",
  resource
)
when {
  resource.organizationId == context.organizationId
};`,
    tags: ['organization', 'isolation', 'multi-tenant']
  },

  // WORKFLOW-SPECIFIC TEMPLATES
  {
    id: 'workflow-execute-member',
    name: 'Member Workflow Execution',
    description: 'Allow members to execute workflows but not modify them.',
    category: 'access',
    icon: FileText,
    cedarPolicy: `permit (
  principal,
  action == Action::"execute",
  resource is Matricks::Workflow
)
when {
  context.userRoles.contains("member")
};`,
    tags: ['workflow', 'execute', 'member']
  },
  {
    id: 'public-workflow-read',
    name: 'Public Workflow Access',
    description: 'Allow anyone to read public workflows.',
    category: 'access',
    icon: Eye,
    cedarPolicy: `permit (
  principal,
  action == Action::"read",
  resource is Matricks::Workflow
)
when {
  resource.isPublic == true
};`,
    tags: ['workflow', 'public', 'read']
  },
  {
    id: 'draft-workflow-owner-only',
    name: 'Draft Workflow Protection',
    description: 'Restrict draft workflows to owners only.',
    category: 'ownership',
    icon: Lock,
    cedarPolicy: `forbid (
  principal,
  action,
  resource is Matricks::Workflow
)
when {
  resource.status == "draft" &&
  resource.ownerId != context.userId
};`,
    tags: ['workflow', 'draft', 'owner-only']
  }
];

// Helper to get templates by category
export function getTemplatesByCategory(category: PolicyCategory): PolicyTemplate[] {
  return policyTemplates.filter(t => t.category === category);
}

// Helper to search templates
export function searchTemplates(query: string): PolicyTemplate[] {
  const lowerQuery = query.toLowerCase();
  return policyTemplates.filter(t =>
    t.name.toLowerCase().includes(lowerQuery) ||
    t.description.toLowerCase().includes(lowerQuery) ||
    t.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}

// Category display info
export const categoryInfo: Record<PolicyCategory, { label: string; description: string }> = {
  'access': {
    label: 'Access Control',
    description: 'Basic read/write/execute permissions'
  },
  'ownership': {
    label: 'Ownership',
    description: 'Owner-based resource control'
  },
  'data-protection': {
    label: 'Data Protection',
    description: 'Sensitive data and field-level security'
  },
  'role-based': {
    label: 'Role-Based',
    description: 'Role-specific permissions'
  }
};
